'use client'

import Image from 'next/image'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { MarkdownViewer } from 'primitives/markdown-viewer'
import { tw } from 'utils/classnames'
import type { SocialComment } from 'codegen/types'

import { useFormatTimeSpan } from '../_hooks/use-format-time-span'
import { IconAccountCircle, IconExpandMore } from '@repo/design-system-cf/components/icons'
import { VerifiedPremiumRibbon } from '@web/app/[locale]/clubhouse/[slug]/_components/verified-premium-ribbon'
import { formatUserName } from '@web/app/[locale]/(web)/masterfeed/utils/format-user-name'
import { sanitizeText } from '@web/app/[locale]/clubhouse/[slug]/(social)/discussions/_utils'
import { useComments } from '../_hooks/use-project-comments'
import type { Project } from '../page-client'
import { ProjectCommentVoteButton } from './project-actions/project-comment-vote-button'
import { ProjectCommentButton } from './project-actions/project-comment-button'
import { ProjectCommentInput } from './project-comment-input'
import { DiscussionCommentMedia } from '@web/app/[locale]/clubhouse/[slug]/(social)/discussions/[topicSlug]/[discussionSlug]/_components/discussion-comment-media'
import { PostCommentAction } from './post-comment-action'
import { Link } from '@repo/web-ui/components/link'
import { NEXT_PUBLIC_CF_URL } from '@web/utils/env'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { Button } from '@repo/design-system-cf/components/button'
import { usePhotoNavigation } from '../_hooks/use-photo-navigation'

function ChildCommentBody({
  comment,
  project,
  parentComment,
  level,
  isMobile,
  timeElapsed,
  redirectUrlBase,
  hideInlineImage,
  prevQuery,
  photoHref,
  openPhoto,
  replying,
  onReply,
  onNewComment,
  setReplying,
  onDeleted,
}: {
  comment: SocialComment
  project: Project
  parentComment?: SocialComment
  level: number
  isMobile: boolean
  timeElapsed: string | false | null | undefined
  redirectUrlBase: string
  hideInlineImage: boolean
  prevQuery: string
  photoHref: string | null
  openPhoto: (e: React.MouseEvent) => void
  replying: boolean
  onReply: () => void
  onNewComment: (newComment: SocialComment) => void
  setReplying: (replying: boolean) => void
  onDeleted?: () => void
}) {
  const userFullName = comment.user
    ? formatUserName(comment?.user.firstName, comment?.user.lastName, 'User')
    : 'User'

  return (
    <div className="border my-2 rounded-[12px] py-4 px-4 bg-cf-grey-25 border-[#1313151F] w-full">
      {isMobile ? (
        <div className="flex items-center justify-between mb-1">
          <p className="text-12 text-cf-grey-600 text-nowrap">{timeElapsed}</p>
          <PostCommentAction comment={comment} onDeleted={onDeleted} />
        </div>
      ) : null}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex flex-row gap-0.5">
            <Link
              target="_blank"
              href={`${NEXT_PUBLIC_CF_URL}/crafter/${comment.user.slug}/`}
              className="text-14 font-semibold text-cf-grey-1000 line-clamp-2"
              data-testid="child-comment-user-name"
            >
              {userFullName}
            </Link>
            <VerifiedPremiumRibbon user={comment.user} />
          </div>
          <p className="text-12 text-cf-grey-600">{comment.user.superSkill}</p>
        </div>

        {!isMobile ? (
          <div className="flex-row items-center justify-start gap-4 flex">
            <p className="text-12 my-3 text-cf-grey-600 text-nowrap">{timeElapsed}</p>
            <PostCommentAction comment={comment} onDeleted={onDeleted} />
          </div>
        ) : null}
      </div>
      <>
        <div
          style={{ overflowWrap: 'anywhere' }}
          className="text-16 text-cf-grey-800 break-anywhere whitespace-normal my-3"
          data-testid="child-comment-content"
        >
          <MarkdownViewer enableHttpsLinkDetection={true} redirectUrlBase={redirectUrlBase}>
            {sanitizeText(comment.content || '')}
          </MarkdownViewer>
        </div>
        <DiscussionCommentMedia
          assets={comment.assets}
          commentId={comment.id}
          hideInline={hideInlineImage}
          prevQuery={prevQuery}
          photoHref={photoHref}
          onOpenPhoto={openPhoto}
        />

        <div className={tw('flex flex-row gap-2', replying ? 'mb-3' : 'mb-0')}>
          <ProjectCommentVoteButton
            comment={comment}
            project={project}
            parentCommentId={parentComment?.id}
          />
          <ProjectCommentButton
            onClick={onReply}
            size="small"
            commentCount={comment.commentsCount || 0}
          />
        </div>
        <ProjectCommentInput
          open={replying}
          projectData={project}
          // on 3rd level reply button will actually reply to the 2nd level comment
          parentComment={level < 3 ? comment : parentComment}
          onSubmit={onNewComment}
          setOpen={setReplying}
        />
      </>
    </div>
  )
}

// oxlint-disable-next-line complexity
function ProjectChildCommentInner({
  comment,
  project,
  parentComment,
  level = 1,
  index,
  numberOfReplies,
  onCommentUpdate,
  onParentUpdate,
  onBumpCommentsCount,
  onDecrementCommentsCount,
  hasMore,
}: {
  comment: SocialComment
  project: Project
  parentComment?: SocialComment
  level?: number
  index?: number
  numberOfReplies?: number
  onCommentUpdate?: () => void
  onParentUpdate?: (newComment: SocialComment) => void
  onBumpCommentsCount?: () => void
  onDecrementCommentsCount?: () => void
  hasMore?: boolean
}) {
  const t = useTranslations('pages.clubhouse.discussions')
  const [replying, setReplying] = React.useState(false)
  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')

  const formatTimeSpan = useFormatTimeSpan()
  const timeElapsed = React.useMemo(
    () =>
      comment.createTime &&
      formatTimeSpan(new Date().getTime() - new Date(comment.createTime).getTime()),
    [comment.createTime, formatTimeSpan],
  )

  const handleReply = () => {
    setReplying(!replying)
  }

  const {
    comments: repliesToComments,
    hasNextPage,
    handleLoadMore,
    handleNewComment,
    bumpCommentReplyCount,
    decrementCommentReplyCount,
    isFetchingNextPage: isFetchingNextPageReplies,
  } = useComments(
    project.id,
    level < 3 ? comment.id : parentComment?.id,
    (comment.commentsCount ?? 0) > 0,
  )

  const firstAsset = comment.assets?.[0]
  const { prevQuery, photoHref, hideInlineImage, redirectUrlBase, openPhoto } = usePhotoNavigation({
    postSlug: project.postSlug,
    commentId: comment.id,
    firstAssetId: firstAsset?.id,
  })

  const showVerticalLine =
    !!index &&
    !!numberOfReplies &&
    (index <= numberOfReplies - 1 || hasMore || isFetchingNextPageReplies)
  const verticalHeight =
    hasMore || isFetchingNextPageReplies ? 'h-[calc(100%)]' : 'h-[calc(100%+32px)]'
  const baseLevel = level ?? 1
  const elbowTranslateX = baseLevel === 1 ? 'translate-x-[55px]' : 'translate-x-[95px]'

  return (
    <div className={tw('flex flex-col w-full relative')}>
      {/* vertical lines for levels */}
      {showVerticalLine && level === 1 ? (
        <div
          className={tw(
            'absolute left-[15px] top-0 bottom-0 w-[2px] bg-cf-grey-250',
            verticalHeight,
          )}
        />
      ) : null}

      {showVerticalLine && level === 2 ? (
        <div
          className={tw(
            'absolute left-[55px] top-0 bottom-0 w-[2px] bg-cf-grey-250',
            verticalHeight,
          )}
        />
      ) : null}

      {showVerticalLine && level === 3 ? (
        <div
          className={tw(
            'absolute left-[95px] top-0 bottom-0 w-[2px] bg-cf-grey-250',
            verticalHeight,
          )}
        />
      ) : null}

      <div className=" flex flex-row gap-x-1" data-testid="project-child-comment">
        {/* tab shift for each level */}
        {Array.from({ length: level ?? 0 }).map((_, i) => (
          <div key={i} className="w-[36px] flex-shrink-0 min-h-full" />
        ))}
        <div className="flex-shrink-0 relative">
          <div className="absolute top-0 translate-y-[20px] -translate-x-[25px] w-[21px] h-[58px] tablet:h-7 rounded-bl-2 border-l-2 border-b-2 border-cf-grey-250" />
          {comment.content?.toString().includes('This comment has been removed.') ? (
            <div className="flex items-center justify-center rounded-full bg-cf-grey-500 mt-5 size-8">
              <IconAccountCircle
                className="text-cf-white size-5"
                data-testid="child-comment-user-avatar"
              />
            </div>
          ) : (
            <div className="relative size-8">
              <Image
                src={comment.user.avatarUrl || ''}
                alt="avatar"
                width={32}
                height={32}
                data-testid="child-comment-user-avatar"
                className="rounded-full mt-[60px] tablet:mt-7 size-8"
              />
              <div className="absolute inset-0 rounded-full border border-[#1313151F]" />
            </div>
          )}
          {repliesToComments && repliesToComments.length > 0 && level < 3 ? (
            <div className="w-full h-[calc(100%-22px)] flex justify-center">
              <div className="border-cf-grey-250 border w-[2px] h-full" />
            </div>
          ) : null}
        </div>
        {comment.content?.toString().includes('This comment has been removed.') ? (
          <div className="border my-2 rounded-[12px] p-4 bg-cf-white border-[#1313151F] w-full">
            <div className="text-cf-grey-500 text-16 italic">{t('comment_deleted')}</div>
          </div>
        ) : (
          <ChildCommentBody
            comment={comment}
            project={project}
            parentComment={parentComment}
            level={level}
            isMobile={isMobile}
            timeElapsed={timeElapsed}
            redirectUrlBase={redirectUrlBase}
            hideInlineImage={hideInlineImage}
            prevQuery={prevQuery}
            photoHref={photoHref}
            openPhoto={openPhoto}
            replying={replying}
            onReply={handleReply}
            onNewComment={newComment => {
              if (level < 3) {
                handleNewComment(newComment)
                bumpCommentReplyCount(comment.id)
              } else {
                onParentUpdate?.(newComment)
              }
              onBumpCommentsCount?.()
            }}
            setReplying={setReplying}
            onDeleted={() => {
              if (parentComment?.id) {
                decrementCommentReplyCount(parentComment.id)
              }
              onDecrementCommentsCount?.()
            }}
          />
        )}
      </div>
      {/* until 3rd level recursion */}
      <div className="relative" data-testid="project-child-comments-container">
        {(level || 1) < 3 ? (
          <>
            {repliesToComments.map((reply, index) => (
              <ProjectChildComment
                key={reply.id}
                level={(level || 1) + 1}
                comment={reply}
                parentComment={comment}
                project={project}
                index={index + 1}
                numberOfReplies={repliesToComments.length}
                onCommentUpdate={onCommentUpdate}
                onParentUpdate={newComment => {
                  handleNewComment(newComment)
                  bumpCommentReplyCount(comment.id)
                  onBumpCommentsCount?.()
                }}
                onBumpCommentsCount={onBumpCommentsCount}
                onDecrementCommentsCount={onDecrementCommentsCount}
                hasMore={hasNextPage || isFetchingNextPageReplies}
              />
            ))}

            {hasNextPage || isFetchingNextPageReplies ? (
              <div className="flex flex-row gap-x-1 mt-1 relative">
                {/* indent to same level as the child replies (level + 1) */}
                <div
                  className={tw(
                    'absolute top-0 -translate-y-1 w-[21px] sm:h-[58px] h-[28px] tablet:h-7 rounded-bl-2 border-l-2 border-b-2 border-cf-grey-250',
                    elbowTranslateX,
                  )}
                />
                {Array.from({ length: (level || 1) + 1 }).map((_, i) => (
                  <div key={i} className="w-[36px] flex-shrink-0" />
                ))}

                <div className="flex-1">
                  <Button
                    variant="tertiary"
                    size="small"
                    className="mt-2 mb-4"
                    onClick={handleLoadMore}
                    loading={isFetchingNextPageReplies}
                    iconRight={<IconExpandMore />}
                  >
                    View more
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}

export function ProjectChildComment(props: {
  comment: SocialComment
  project: Project
  parentComment?: SocialComment
  level?: number
  index?: number
  numberOfReplies?: number
  onCommentUpdate?: () => void
  onParentUpdate?: (newComment: SocialComment) => void
  onBumpCommentsCount?: () => void
  onDecrementCommentsCount?: () => void
  hasMore?: boolean
}) {
  return (
    <React.Suspense>
      <ProjectChildCommentInner {...props} />
    </React.Suspense>
  )
}
